import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { Gauge } from './Gauge';
import kloudynetLogo from '@assets/image_1777160759976.png';
import { useCustomers } from '../../context/CustomerContext';
import { ExecutiveSummary } from './ExecutiveSummary';
import { MitreCoverageMatrix } from './MitreCoverageMatrix';
import {
  InsiderThreatSpotlight,
  type InsiderThreatUser,
} from './InsiderThreatSpotlight';
import { KillChainStories } from './KillChainStories';
import { RuleEffectiveness } from './RuleEffectiveness';
import { ReportFooter } from './ReportView';

// ── Types ────────────────────────────────────────────────────────────────────

export interface WeeklyReportData {
  customerId: string;
  timezone: string;
  weekStart: string;
  weekEnding: string;
  generatedAt: string;
  homeCountry: string;
  kpis: {
    totalIncidents: number;
    highSeverityIncidents: number;
    avgTriageMinutes: number | null;
    avgCloseMinutes: number | null;
  };
  severity: { severity: string; count: number }[];
  status: { status: string; count: number }[];
  classification: { classification: string; count: number }[];
  topIncidents: { title: string; count: number }[];
  mitre: { tactic: string; count: number }[];
  singleFactorAuth: { user: string; attempts: number; country: string }[];
  maliciousSignIn: { user: string; attempts: number; locations: string[] }[];
  failedSignIn: { resultType: number; description: string; count: number }[];
  threatIntel: { indicatorType: string; count: number }[];
  torIp: {
    sourceIp: string; count: number;
    activities: string[]; destinations: string[]; computers: string[];
  }[];
  /**
   * Insider-threat / data-exfiltration spotlight.
   * Optional for backward compatibility with cached/older payloads - newer
   * server responses always include it (with empty `users[]` when no
   * thresholds are crossed and `sourceErrors` flagging any failed sub-query).
   */
  insiderThreat?: {
    users: InsiderThreatUser[];
    sourceErrors: { downloads: boolean; deletes: boolean; afterHours: boolean; oauth: boolean };
  };
  /** Per-query failure flag (boolean only - server never leaks raw errors). */
  errors: Record<string, boolean>;
}

interface Props {
  report: WeeklyReportData;
  customerName: string;
}

// ── Palette (mirrors the Logic-App template's CSS variables) ─────────────────
const C = {
  primary: '#4c81d1',
  primaryDark: '#2c3e50',
  danger: '#dc3545',
  warning: '#fd7e14',
  success: '#28a745',
  info: '#17a2b8',
  high: '#dc3545',
  medium: '#fd7e14',
  low: '#28a745',
  informational: '#17a2b8',
};

const SEVERITY_COLORS: Record<string, string> = {
  High: C.high,
  Medium: C.medium,
  Low: C.low,
  Informational: C.informational,
};

const STATUS_COLORS: Record<string, string> = {
  Active: C.danger,
  New: C.warning,
  Closed: C.success,
};

const CLASSIFICATION_COLORS: Record<string, string> = {
  TruePositive: C.danger,
  BenignPositive: C.success,
  FalsePositive: C.warning,
  Undetermined: C.info,
};

const TI_COLORS = ['#4c81d1', '#2c3e50', '#dc3545', '#fd7e14', '#28a745', '#17a2b8', '#6c757d'];

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtMinutes(m: number | null): string {
  if (m === null || !Number.isFinite(m)) return ' - ';
  if (m < 60) return `${m.toFixed(1)} min`;
  const h = m / 60;
  if (h < 24) return `${h.toFixed(1)} hr`;
  const d = h / 24;
  return `${d.toFixed(1)} d`;
}

function triageBadge(m: number | null): { label: string; bg: string } {
  if (m === null) return { label: ' - ', bg: '#6c757d' };
  if (m < 15) return { label: 'EXCELLENT', bg: C.success };
  if (m < 60) return { label: 'GOOD', bg: C.info };
  if (m < 240) return { label: 'NEEDS ATTENTION', bg: C.warning };
  return { label: 'CRITICAL', bg: C.danger };
}

function fmtLongDate(ymd: string): string {
  // ymd = YYYY-MM-DD; render "April 27, 2026"
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${months[m - 1]} ${d}, ${y}`;
}

function fmtGeneratedAt(iso: string, tz: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric', month: 'long', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
      timeZoneName: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

// ── Subcomponents ────────────────────────────────────────────────────────────

function SectionHeader({ title, tone = 'primary' }: { title: string; tone?: 'primary' | 'danger' | 'warning' | 'success' }) {
  const bg = tone === 'danger'
    ? `linear-gradient(135deg, #7f1d1d 0%, ${C.danger} 100%)`
    : tone === 'warning'
      ? `linear-gradient(135deg, #9a3412 0%, ${C.warning} 100%)`
      : tone === 'success'
        ? `linear-gradient(135deg, #14532d 0%, ${C.success} 100%)`
        : `linear-gradient(135deg, ${C.primaryDark} 0%, ${C.primary} 100%)`;
  return (
    <div className="wr-section-header" style={{ background: bg }}>{title}</div>
  );
}

function AlertBox({
  variant, title, children,
}: { variant: 'success' | 'warning' | 'danger' | 'info'; title?: string; children: React.ReactNode }) {
  const styles: Record<string, { bg: string; border: string; color: string }> = {
    success: { bg: '#d4edda', border: '#28a745', color: '#155724' },
    warning: { bg: '#fff3cd', border: '#ffc107', color: '#856404' },
    danger:  { bg: '#f8d7da', border: '#dc3545', color: '#721c24' },
    info:    { bg: '#d1ecf1', border: '#17a2b8', color: '#0c5460' },
  };
  const s = styles[variant];
  return (
    <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderLeft: `4px solid ${s.border}`, borderRadius: 8, padding: '12px 16px', margin: '12px 0', color: s.color, fontSize: 14 }}>
      {title && <div style={{ fontWeight: 700, marginBottom: 4 }}>{title}</div>}
      <div>{children}</div>
    </div>
  );
}

function RiskPill({ level }: { level: 'critical' | 'high' | 'medium' | 'low' }) {
  const map = {
    critical: { bg: '#f8d7da', color: '#721c24', label: 'Critical' },
    high:     { bg: '#f8d7da', color: '#721c24', label: 'High' },
    medium:   { bg: '#fff3cd', color: '#856404', label: 'Medium' },
    low:      { bg: '#d4edda', color: '#155724', label: 'Low' },
  } as const;
  const s = map[level];
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', background: s.bg, color: s.color, borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: 0.4 }}>
      {s.label}
    </span>
  );
}

function SeverityPill({ severity }: { severity: string }) {
  const key = severity.toLowerCase();
  const colors: Record<string, { bg: string; color: string }> = {
    high:          { bg: '#f8d7da', color: '#721c24' },
    medium:        { bg: '#fff3cd', color: '#856404' },
    low:           { bg: '#d4edda', color: '#155724' },
    informational: { bg: '#d1ecf1', color: '#0c5460' },
  };
  const s = colors[key] ?? { bg: '#e9ecef', color: '#495057' };
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', background: s.bg, color: s.color, borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: 0.4 }}>
      {severity}
    </span>
  );
}

// ── Main view ────────────────────────────────────────────────────────────────

export function WeeklyReportView({ report, customerName }: Props) {
  const { activeCustomer } = useCustomers();
  const { kpis } = report;
  const triBadge = triageBadge(kpis.avgTriageMinutes);

  return (
    <div className="weekly-report-root">
      <style>{`
        .weekly-report-root { font-family: "Segoe UI", system-ui, -apple-system, sans-serif; color: #333; background: #f5f7fa; padding: 24px; border-radius: 16px; }
        .wr-header { background: linear-gradient(135deg, ${C.primaryDark} 0%, ${C.primary} 100%); color: #fff; padding: 28px 32px; border-radius: 16px; margin-bottom: 24px; box-shadow: 0 6px 24px rgba(44,62,80,0.18); }
        .wr-header h1 { margin: 0 0 6px; font-size: 1.7em; letter-spacing: 0.4px; font-weight: 600; }
        .wr-header .wr-subtitle { font-size: 0.95em; opacity: 0.92; }
        .wr-header .wr-period { margin-top: 14px; font-size: 0.85em; opacity: 0.85; border-top: 1px solid rgba(255,255,255,0.18); padding-top: 12px; }
        .wr-section { background: #fff; border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.07); margin-bottom: 24px; overflow: hidden; }
        .wr-section-header { color: #fff; padding: 16px 24px; font-size: 1.05em; font-weight: 600; letter-spacing: 0.3px; }
        .wr-section-content { padding: 24px; }
        .wr-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .wr-table th { text-align: left; padding: 10px 12px; background: #f8fafc; color: ${C.primaryDark}; font-weight: 600; border-bottom: 2px solid #e5e7eb; }
        .wr-table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; color: #1f2937; vertical-align: top; }
        .wr-table tr:last-child td { border-bottom: none; }
        .wr-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
        .wr-kpi-card { background: #fff; border-radius: 14px; padding: 18px 20px; box-shadow: 0 3px 14px rgba(0,0,0,0.07); border-left: 5px solid ${C.primary}; }
        .wr-kpi-card.danger { border-left-color: ${C.danger}; }
        .wr-kpi-card.success { border-left-color: ${C.success}; }
        .wr-kpi-card.warning { border-left-color: ${C.warning}; }
        .wr-kpi-label { font-size: 0.75em; text-transform: uppercase; letter-spacing: 0.6px; color: #6c757d; font-weight: 600; }
        .wr-kpi-value { font-size: 1.85em; font-weight: 700; color: ${C.primaryDark}; margin-top: 6px; line-height: 1.1; }
        .wr-kpi-sub { font-size: 0.78em; color: #6c757d; margin-top: 4px; }
        .wr-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
        .wr-grid-2 > div { min-width: 0; }
        .wr-rec-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
        .wr-rec-card { background: #f8fafc; border-radius: 12px; padding: 16px 18px; border-left: 4px solid ${C.primary}; }
        .wr-rec-card h4 { margin: 0 0 6px; font-size: 0.95em; color: ${C.primaryDark}; font-weight: 700; }
        .wr-rec-card p { margin: 0; font-size: 0.85em; color: #495057; line-height: 1.5; }
        .wr-footer { text-align: center; color: #6c757d; font-size: 0.78em; padding: 18px 0 4px; }
        .wr-no-data { padding: 24px; color: #6c757d; font-size: 0.9em; text-align: center; }
        .wr-error-banner { background: #fff3cd; border: 1px solid #ffc107; border-left: 4px solid #ffc107; color: #856404; padding: 10px 14px; border-radius: 8px; font-size: 12px; margin-bottom: 16px; }
        @media (max-width: 720px) {
          .wr-grid-2 { grid-template-columns: 1fr; }
          .wr-kpi-grid { grid-template-columns: repeat(2, 1fr); }
          .wr-rec-grid { grid-template-columns: 1fr; }
        }
        @media print {
          .weekly-report-root { background: #fff; padding: 0; }
          .wr-section { box-shadow: none; border: 1px solid #e5e7eb; page-break-inside: avoid; }
          .wr-header { box-shadow: none; }
        }
      `}</style>

      {/* Gradient header - mirrors the Daily report banner: logo box on the
          left, centered title + customer subtitle, WORKSPACE pill on the
          right. The "Reporting Period | Generated" line moves OUT of the
          header to a separate italic subline, exactly like the daily view. */}
      <div className="wr-header">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="rounded-lg bg-white px-4 py-2.5">
            <img src={kloudynetLogo} alt="Kloudynet" style={{ height: 44, display: 'block' }} />
          </div>
          <div className="flex-1 text-center min-w-[240px]">
            <h1 style={{ fontSize: '2.1em', fontWeight: 300, marginBottom: 6, textShadow: '2px 2px 4px rgba(0,0,0,0.2)' }}>
              Weekly Security Operations Report
            </h1>
            <p style={{ opacity: 0.92, fontSize: '1.05em', margin: 0 }}>{customerName}</p>
          </div>
          {activeCustomer?.logoUrl && (
            <div className="rounded-lg bg-white px-4 py-2.5">
              <img src={activeCustomer.logoUrl} alt={customerName} style={{ height: 44, display: 'block' }} />
            </div>
          )}
        </div>
      </div>

      <div className="text-center text-slate-500 italic mb-6 text-sm">
        Report Period: {fmtLongDate(report.weekStart)} – {fmtLongDate(report.weekEnding)}
        &nbsp;|&nbsp;
        Generated: {fmtGeneratedAt(report.generatedAt, report.timezone)}
      </div>

      <ExecutiveSummary period="weekly" snapshot={report} cacheKey={report.generatedAt} />

      {(() => {
        // The TOR exit-node enrichment relies on a KQL `externaldata` call
        // out to torproject.org, which most enterprise Sentinel workspaces
        // block by policy. Treating that as a "data source unavailable"
        // makes every report look broken, so we surface it separately as an
        // expected limitation and only raise the alarming partial-data banner
        // for *other* failures (e.g. older workspaces where the
        // ThreatIntelIndicators schema differs).
        const realErrors = Object.entries(report.errors)
          .filter(([key, val]) => val && key !== 'torIp')
          .map(([key]) => key);
        // Total queries = every key the server reported on, minus the TOR
        // exit-node enrichment (handled separately below). Derived from the
        // payload itself so backend changes can never desync the banner.
        const totalQueries = Math.max(
          1,
          Object.keys(report.errors).filter((k) => k !== 'torIp').length,
        );
        const torBlocked = Boolean(report.errors['torIp']);
        return (
          <>
            {realErrors.length > 0 && (
              <div className="wr-error-banner">
                <strong>Partial data:</strong> {realErrors.length} of {totalQueries} data sources
                {realErrors.length === 1 ? ' is' : ' are'} unavailable. Affected sections are
                flagged individually below. Common cause: the threat-intel table schema differs in
                older workspaces.
              </div>
            )}
            {torBlocked && (
              <div
                style={{
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  color: '#1e3a8a',
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontSize: 13,
                  marginBottom: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span aria-hidden="true">ⓘ</span>
                <span>
                  TOR exit-node enrichment is disabled - this workspace blocks the outbound
                  lookup to torproject.org (an expected hardening choice). The rest of the
                  report is unaffected.
                </span>
              </div>
            )}
          </>
        );
      })()}

      {/* KPI strip */}
      <div className="wr-kpi-grid">
        <div className="wr-kpi-card">
          <div className="wr-kpi-label">Total Security Incidents</div>
          <div className="wr-kpi-value">{kpis.totalIncidents.toLocaleString()}</div>
          <div className="wr-kpi-sub">Past 7 days</div>
        </div>
        <div className="wr-kpi-card danger">
          <div className="wr-kpi-label">High Severity Incidents</div>
          <div className="wr-kpi-value" style={{ color: C.danger }}>{kpis.highSeverityIncidents.toLocaleString()}</div>
          <div className="wr-kpi-sub">Critical attention required</div>
        </div>
        <div className="wr-kpi-card success">
          <div className="wr-kpi-label">Avg Time to Triage</div>
          <div className="wr-kpi-value">{fmtMinutes(kpis.avgTriageMinutes)}</div>
          <div className="wr-kpi-sub">
            <span style={{ display: 'inline-block', marginTop: 2, padding: '2px 8px', borderRadius: 999, background: triBadge.bg, color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>
              {triBadge.label}
            </span>
          </div>
        </div>
        <div className="wr-kpi-card warning">
          <div className="wr-kpi-label">Avg Time to Close</div>
          <div className="wr-kpi-value">{fmtMinutes(kpis.avgCloseMinutes)}</div>
          <div className="wr-kpi-sub">From creation to closure</div>
        </div>
      </div>

      {/* SECTION 1: Response Performance Dashboard
         Gauge calibration mirrors the source Logic-App template:
         - Triage: range 0–60 min, bands 0/15/30/60, threshold red @ 60 min.
         - Close:  range 0–max(120, value*1.2) min, bands 0/30/60, threshold @ 60. */}
      <div className="wr-section">
        <SectionHeader title="Response Performance Dashboard" />
        <div className="wr-section-content">
          {report.errors['triageAndClose'] && (
            <AlertBox variant="warning" title="Query unavailable">
              The triage / closure query failed against the customer's workspace. Numbers below
              fall back to zero and should not be interpreted as actual SOC performance.
            </AlertBox>
          )}
          <div className="wr-grid-2">
            <Gauge
              value={kpis.avgTriageMinutes}
              unit="min"
              label="Avg Triage Time"
              bands={[15, 30, 60]}
              max={60}
              dtick={15}
              thresholdValue={60}
            />
            <Gauge
              value={kpis.avgCloseMinutes}
              unit="min"
              label="Avg Close Time"
              bands={[30, 60, Math.max(120, (kpis.avgCloseMinutes ?? 0) * 1.2)]}
              max={Math.max(120, (kpis.avgCloseMinutes ?? 0) * 1.2)}
              dtick={30}
              thresholdValue={60}
            />
          </div>
          <AlertBox variant="info" title="Performance Summary">
            Triage and resolution metrics measured against incidents handled by SOC analysts.
            Lower values indicate faster response. Response-time target: under 60 minutes for High severity.
            Resolution-time target: under 24 hours for High severity.
          </AlertBox>
        </div>
      </div>

      {/* SECTION 2: Incident Analysis (severity + status side-by-side) */}
      <div className="wr-section">
        <SectionHeader title="Incident Analysis" />
        <div className="wr-section-content">
          {(report.errors['severity'] || report.errors['status']) && (
            <AlertBox variant="warning" title="Query unavailable">
              {[report.errors['severity'] && 'severity', report.errors['status'] && 'status']
                .filter(Boolean).join(' & ')} query failed; affected pane(s) below show no data.
            </AlertBox>
          )}
          <div className="wr-grid-2">
            <div>
              <h3 style={{ color: C.primaryDark, marginTop: 0, marginBottom: 12, fontSize: '1em' }}>By Severity</h3>
              <SeverityTable rows={report.severity} />
              <div style={{ height: 220, marginTop: 12 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={report.severity} dataKey="count" nameKey="severity" innerRadius={50} outerRadius={85} paddingAngle={2}>
                      {report.severity.map((row, i) => (
                        <Cell key={i} fill={SEVERITY_COLORS[row.severity] ?? '#6c757d'} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div>
              <h3 style={{ color: C.primaryDark, marginTop: 0, marginBottom: 12, fontSize: '1em' }}>By Status</h3>
              <StatusTable rows={report.status} />
              <div style={{ height: 220, marginTop: 12 }}>
                <ResponsiveContainer>
                  <BarChart data={report.status} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="status" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {report.status.map((row, i) => (
                        <Cell key={i} fill={STATUS_COLORS[row.status] ?? C.primary} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 3: Incident Classification */}
      <div className="wr-section">
        <SectionHeader title="Incident Classification" />
        <div className="wr-section-content">
          {report.errors['classification'] && (
            <AlertBox variant="warning" title="Query unavailable">
              The classification query failed; this section shows no data.
            </AlertBox>
          )}
          {report.classification.length === 0 ? (
            <div className="wr-no-data">
              {report.errors['classification']
                ? 'Data unavailable.'
                : 'No classified incidents in this period.'}
            </div>
          ) : (
            <div className="wr-grid-2">
              <div>
                <table className="wr-table">
                  <thead><tr><th>Classification</th><th style={{ textAlign: 'center' }}>Count</th></tr></thead>
                  <tbody>
                    {report.classification.map((row) => (
                      <tr key={row.classification}>
                        <td>{row.classification}</td>
                        <td style={{ textAlign: 'center' }}>{row.count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ height: 280 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={report.classification} dataKey="count" nameKey="classification" outerRadius={100} label>
                      {report.classification.map((row, i) => (
                        <Cell key={i} fill={CLASSIFICATION_COLORS[row.classification] ?? TI_COLORS[i % TI_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* SECTION 4: Top 10 Security Incidents */}
      <div className="wr-section">
        <SectionHeader title="Top 10 Security Incidents" />
        <div className="wr-section-content">
          {report.errors['topIncidents'] && (
            <AlertBox variant="warning" title="Query unavailable">
              The top-incidents query failed; this section shows no data.
            </AlertBox>
          )}
          {report.topIncidents.length === 0 ? (
            <div className="wr-no-data">
              {report.errors['topIncidents'] ? 'Data unavailable.' : 'No incidents in this period.'}
            </div>
          ) : (
            <>
              <div style={{ height: 320, marginBottom: 16 }}>
                <ResponsiveContainer>
                  <BarChart data={report.topIncidents} margin={{ top: 8, right: 24, left: 0, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="title"
                      fontSize={10}
                      angle={-30}
                      textAnchor="end"
                      interval={0}
                      tickFormatter={(t: string) => (t.length > 30 ? t.slice(0, 28) + '…' : t)}
                    />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Bar dataKey="count" fill={C.primary} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <table className="wr-table">
                <thead><tr><th>Incident Type</th><th style={{ textAlign: 'center' }}>Count</th><th style={{ textAlign: 'center' }}>Priority</th></tr></thead>
                <tbody>
                  {report.topIncidents.map((row, i) => (
                    <tr key={i}>
                      <td>{row.title}</td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.count.toLocaleString()}</td>
                      <td style={{ textAlign: 'center' }}><RiskPill level="critical" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>

      {/* SECTION 5: Top 10 Attack Tactics (MITRE ATT&CK) */}
      <div className="wr-section">
        <SectionHeader title="Top 10 Attack Tactics (MITRE ATT&CK)" tone="warning" />
        <div className="wr-section-content">
          <AlertBox variant="warning" title="Primary Threat Vector">
            Focus defensive measures on the most prevalent attack tactics observed during this reporting period.
          </AlertBox>
          {report.errors['mitre'] && (
            <AlertBox variant="warning" title="Query unavailable">
              The attack-tactic query failed; this section shows no data.
            </AlertBox>
          )}
          {/* Coverage matrix headlines the section: shows the full 14 enterprise
              tactics including zero-activity gaps so coverage is visible. */}
          <MitreCoverageMatrix
            title="ATT&CK Coverage (This Week)"
            periodLabel="this week"
            data={report.mitre}
          />
          {report.mitre.length === 0 ? (
            <div className="wr-no-data">
              {report.errors['mitre'] ? 'Data unavailable.' : 'No incidents tagged with an attack tactic in this period.'}
            </div>
          ) : (
            <>
              <div style={{ height: Math.max(240, report.mitre.length * 32 + 60), marginBottom: 16 }}>
                <ResponsiveContainer>
                  <BarChart data={[...report.mitre].reverse()} layout="vertical" margin={{ top: 8, right: 24, left: 100, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" fontSize={11} />
                    <YAxis type="category" dataKey="tactic" fontSize={11} width={140} />
                    <Tooltip />
                    <Bar dataKey="count" fill={C.warning} radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <table className="wr-table">
                <thead><tr><th>Tactic</th><th style={{ textAlign: 'center' }}>Count</th><th style={{ textAlign: 'center' }}>Risk Level</th></tr></thead>
                <tbody>
                  {report.mitre.map((row, i) => (
                    <tr key={i}>
                      <td>{row.tactic}</td>
                      <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.count.toLocaleString()}</td>
                      <td style={{ textAlign: 'center' }}><RiskPill level="high" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>

      {/* SECTION 6: Threat Intelligence Overview */}
      <div className="wr-section">
        <SectionHeader title="Threat Intelligence Overview" />
        <div className="wr-section-content">
          {report.errors['threatIntel'] && (
            <AlertBox variant="warning" title="Query unavailable">
              The ThreatIntelIndicators table query failed (most likely the table is not provisioned
              in this workspace, or its schema differs).
            </AlertBox>
          )}
          {report.threatIntel.length === 0 ? (
            <div className="wr-no-data">
              {report.errors['threatIntel']
                ? 'Data unavailable.'
                : 'No active threat-intelligence indicators in this period.'}
            </div>
          ) : (
            <div className="wr-grid-2">
              <div>
                <table className="wr-table">
                  <thead><tr><th>Indicator Type</th><th style={{ textAlign: 'center' }}>Count</th></tr></thead>
                  <tbody>
                    {report.threatIntel.map((row, i) => (
                      <tr key={i}>
                        <td>{row.indicatorType}</td>
                        <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ height: 320 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={report.threatIntel} dataKey="count" nameKey="indicatorType" outerRadius={110} label={(p: { indicatorType?: string }) => p.indicatorType ?? ''}>
                      {report.threatIntel.map((_, i) => (
                        <Cell key={i} fill={TI_COLORS[i % TI_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* SECTION 7: User Activity Analysis */}
      <div className="wr-section">
        <SectionHeader title="User Activity Analysis" tone="danger" />
        <div className="wr-section-content">
          <h3 style={{ color: C.danger, margin: '0 0 12px', fontSize: '1em' }}>
            Top 5 Failed Sign-in Analysis
            {report.errors['failedSignIn'] && (
              <span style={{ marginLeft: 10, fontSize: 11, color: '#856404', background: '#fff3cd', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>Query unavailable</span>
            )}
          </h3>
          {report.failedSignIn.length === 0 ? (
            <div className="wr-no-data">
              {report.errors['failedSignIn'] ? 'Data unavailable.' : 'No failed sign-ins recorded.'}
            </div>
          ) : (
            <table className="wr-table">
              <thead>
                <tr>
                  <th>Error Code</th>
                  <th style={{ textAlign: 'center' }}>Count</th>
                  <th>Description</th>
                  <th style={{ textAlign: 'center' }}>Risk</th>
                </tr>
              </thead>
              <tbody>
                {report.failedSignIn.map((row, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'ui-monospace, monospace' }}>{row.resultType}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.count.toLocaleString()}</td>
                    <td style={{ fontSize: 12, color: '#475569' }}>{row.description}</td>
                    <td style={{ textAlign: 'center' }}><RiskPill level="critical" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3 style={{ color: C.danger, margin: '28px 0 12px', fontSize: '1em' }}>
            Top 10 Malicious Sign-in Attempts
            {report.errors['maliciousSignIn'] && (
              <span style={{ marginLeft: 10, fontSize: 11, color: '#856404', background: '#fff3cd', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>Query unavailable</span>
            )}
          </h3>
          {report.maliciousSignIn.length === 0 ? (
            <div className="wr-no-data">
              {report.errors['maliciousSignIn']
                ? 'Data unavailable.'
                : 'No malicious sign-in attempts (IP-blocked) recorded.'}
            </div>
          ) : (
            <table className="wr-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th style={{ textAlign: 'center' }}>Attempts</th>
                  <th>Locations</th>
                  <th style={{ textAlign: 'center' }}>Risk</th>
                </tr>
              </thead>
              <tbody>
                {report.maliciousSignIn.map((row, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 500 }}>{row.user}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.attempts.toLocaleString()}</td>
                    <td style={{ fontSize: 12, color: '#475569' }}>{row.locations.join(', ')}</td>
                    <td style={{ textAlign: 'center' }}><RiskPill level="critical" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3 style={{ color: C.warning, margin: '28px 0 12px', fontSize: '1em' }}>
            Top 10 Single Factor Authentication Risks
            {report.errors['singleFactorAuth'] && (
              <span style={{ marginLeft: 10, fontSize: 11, color: '#856404', background: '#fff3cd', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>Query unavailable</span>
            )}
          </h3>
          {report.singleFactorAuth.length === 0 ? (
            <div className="wr-no-data">
              {report.errors['singleFactorAuth']
                ? 'Data unavailable.'
                : 'No single-factor authentication risks recorded.'}
            </div>
          ) : (
            <table className="wr-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th style={{ textAlign: 'center' }}>Attempts</th>
                  <th style={{ textAlign: 'center' }}>Country</th>
                  <th style={{ textAlign: 'center' }}>Risk</th>
                </tr>
              </thead>
              <tbody>
                {report.singleFactorAuth.map((row, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 500 }}>{row.user}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.attempts.toLocaleString()}</td>
                    <td style={{ textAlign: 'center' }}>{row.country || ' - '}</td>
                    <td style={{ textAlign: 'center' }}><RiskPill level="critical" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* SECTION 7a: Top 3 Incidents as Kill-Chain Stories (AI-narrated) */}
      <div className="wr-section">
        <SectionHeader title="Top 3 Incidents - Kill-Chain Walkthroughs" tone="danger" />
        <div className="wr-section-content">
          <KillChainStories
            customerId={report.customerId}
            period="weekly"
            weekEnding={report.weekEnding}
            cacheKey={report.generatedAt}
          />
        </div>
      </div>

      {/* SECTION 7c: Detection-Rule Effectiveness Scorecard */}
      <div className="wr-section">
        <SectionHeader title="Detection-Rule Effectiveness" />
        <div className="wr-section-content">
          <RuleEffectiveness
            customerId={report.customerId}
            period="weekly"
            weekEnding={report.weekEnding}
            cacheKey={report.generatedAt}
          />
        </div>
      </div>

      {/* SECTION 7b: Insider Threat & Data-Exfiltration Spotlight */}
      <div className="wr-section">
        <SectionHeader title="Insider Threat & Data-Exfiltration Spotlight" tone="danger" />
        <div className="wr-section-content">
          <InsiderThreatSpotlight
            users={report.insiderThreat?.users ?? []}
            periodLabel="this week"
            errors={report.insiderThreat?.sourceErrors}
          />
        </div>
      </div>

      {/* SECTION 8: Identity & Access Management (TOR) */}
      <div className="wr-section">
        <SectionHeader title="Identity &amp; Access Management" />
        <div className="wr-section-content">
          <h3 style={{ color: C.primaryDark, margin: '0 0 12px', fontSize: '1em' }}>TOR Exit Node Detection</h3>
          {report.errors['torIp'] && (
            <AlertBox variant="warning" title="TOR Feed Unreachable">
              The external lookup to torproject.org failed in this workspace (typically blocked by
              network policy). TOR-correlation cannot be reported until outbound traffic is
              permitted or a custom threat-intel feed is provided.
            </AlertBox>
          )}
          {!report.errors['torIp'] && report.torIp.length === 0 ? (
            <AlertBox variant="success" title="No TOR Activity Detected">
              No traffic from known TOR exit nodes was observed against this customer in the last 90 days.
            </AlertBox>
          ) : report.torIp.length > 0 ? (
            <table className="wr-table">
              <thead>
                <tr>
                  <th>Source IP</th>
                  <th style={{ textAlign: 'center' }}>Hits</th>
                  <th>Activities</th>
                  <th>Affected Hosts</th>
                  <th style={{ textAlign: 'center' }}>Risk</th>
                </tr>
              </thead>
              <tbody>
                {report.torIp.map((row, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: 'ui-monospace, monospace' }}>{row.sourceIp}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.count.toLocaleString()}</td>
                    <td style={{ fontSize: 12, color: '#475569' }}>{row.activities.slice(0, 4).join(', ')}{row.activities.length > 4 ? '…' : ''}</td>
                    <td style={{ fontSize: 12, color: '#475569' }}>{row.computers.slice(0, 4).join(', ')}{row.computers.length > 4 ? '…' : ''}</td>
                    <td style={{ textAlign: 'center' }}><RiskPill level="critical" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      </div>

      {/* SECTION 9: Security Recommendations (static) */}
      <div className="wr-section">
        <SectionHeader title="Security Recommendations" tone="success" />
        <div className="wr-section-content">
          <div className="wr-rec-grid">
            <div className="wr-rec-card">
              <h4>Enforce Multi-Factor Authentication</h4>
              <p>Require multi-factor authentication (MFA) for everyone signing in interactively, especially the users flagged in the Single-Factor Authentication Risks list above. Use access policies to block high-risk sign-ins.</p>
            </div>
            <div className="wr-rec-card" style={{ borderLeftColor: C.warning }}>
              <h4>Geo-Blocking &amp; Anomaly Detection</h4>
              <p>Block sign-ins (or require extra verification) from countries where the users in the Malicious Sign-in list above were observed. Maintain a list of trusted locations for permitted geographies.</p>
            </div>
            <div className="wr-rec-card" style={{ borderLeftColor: C.danger }}>
              <h4>Continuous Monitoring</h4>
              <p>Maintain 24/7 monitoring of critical security events (target: respond to High-severity incidents within 60 minutes). Tune detection rules to reduce noise from the highest-volume alerts in the Top 10 list.</p>
            </div>
            <div className="wr-rec-card" style={{ borderLeftColor: C.info }}>
              <h4>Threat Intelligence Integration</h4>
              <p>Continue feeding threat-intelligence data into your detection rules. Periodically review the indicator-type breakdown above and confirm that high-volume IP and domain feeds are actually being used by your detection rules.</p>
            </div>
          </div>
        </div>
      </div>

      <ReportFooter generatedAt={report.generatedAt} timezone={report.timezone} />
    </div>
  );
}

// ── Small inline tables ──────────────────────────────────────────────────────

function SeverityTable({ rows }: { rows: { severity: string; count: number }[] }) {
  if (rows.length === 0) return <div className="wr-no-data">No incidents.</div>;
  return (
    <table className="wr-table">
      <thead><tr><th>Severity</th><th style={{ textAlign: 'center' }}>Count</th></tr></thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.severity}>
            <td><SeverityPill severity={row.severity} /></td>
            <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.count.toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StatusTable({ rows }: { rows: { status: string; count: number }[] }) {
  if (rows.length === 0) return <div className="wr-no-data">No incidents.</div>;
  return (
    <table className="wr-table">
      <thead><tr><th>Status</th><th style={{ textAlign: 'center' }}>Count</th></tr></thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.status}>
            <td>{row.status}</td>
            <td style={{ textAlign: 'center', fontWeight: 600 }}>{row.count.toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
